import { connect } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import { Dispatch } from 'redux';
import { parse } from 'query-string';
import { getSearch } from 'connected-react-router';
import { PageSearch, Props } from 'components/pages/PageSearch/PageSearch';
import { fetchSearchContent, resetSearchModule, resetSkipSearchFrontloadRequest } from 'modules/search/actions';
import {
  paginationSelector,
  searchResultsSelector,
  searchResultsLoadingSelector,
  searchResultsFetchedSelector,
  suggestedSelector,
  skipSearchFrontloadRequestSelector,
} from 'modules/search/selectors';
import { isAuthSelector } from 'modules/user/selectors';
import { popularSearchesSelector, getPopularSearches } from 'modules/popularSearches';
import { pageSearchSearchQuerySelector } from 'modules/router';
import { constantsSelector } from 'modules/app/selectors';
import { CONSTANTS } from 'modules/app/constants';
import { DefaultPagination } from 'constants/index';
import { generateFacetString } from 'utils/Analytics';
import { Contentful } from 'mxp-schemas';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchSearchContent(): Promise<void> {
    await dispatch(fetchSearchContent());
  },

  resetSearchModule(): void {
    dispatch(resetSearchModule());
  },

  getPopularSearches(): void {
    dispatch(getPopularSearches());
  },
  resetSkipSearchFrontloadRequest(): void {
    dispatch(resetSkipSearchFrontloadRequest());
  },
});

const mapStateToProps = (state: State.Root) => {
  const search = getSearch(state);
  const {
    sortBy = DefaultPagination.List.SORT_BY,
    page = DefaultPagination.List.PAGE,
    perPage = DefaultPagination.List.MIN_PER_PAGE,
    date,
    topic,
    subtopic,
    category,
    type,
    premium,
    credits,
    getNoticed,
    includedInWebcastPass,
    nasbaField,
    nasbaProgram,
    availableFormat,
    eventStartDate,
    eventEndDate,
    contentSource,
    groups,
    industry,
    skill,
    subskill,
    trend,
    contentLanguage,
    careerStage,
    jobRole,
    location,
    locationByCountry,
    locationByState,
  }: any = parse(search, {
    arrayFormat: 'comma',
    parseNumbers: true,
  });
  const cpeCredits: string[] = credits === 0 ? credits.toString() : credits;
  const searchQuery: string = pageSearchSearchQuerySelector(state);
  const filtersStr: string = [
    date,
    topic,
    subtopic,
    category,
    type,
    premium,
    cpeCredits,
    getNoticed,
    includedInWebcastPass,
    nasbaField,
    nasbaProgram,
    availableFormat,
    eventStartDate,
    eventEndDate,
    contentSource,
    groups,
    industry,
    skill,
    subskill,
    trend,
    contentLanguage,
    careerStage,
    jobRole,
    location,
    locationByCountry,
    locationByState,
  ]
    .filter(Boolean)
    .join();
  const analyticsFacets = generateFacetString([
    ['date', date],
    ['top', topic],
    ['sub', subtopic],
    ['industry', industry],
    ['skill', skill],
    ['trend', trend],
    ['cat', category],
    ['type', type],
    ['premium', premium],
    ['credits', cpeCredits],
    ['getNoticed', getNoticed],
    ['includedInWebcastPass', includedInWebcastPass],
    ['nasbaField', nasbaField],
    ['nasbaProgram', nasbaProgram],
    ['availableFormat', availableFormat],
    ['eventStartDate', eventStartDate],
    ['eventEndDate', eventEndDate],
    ['contentSource', contentSource],
    ['groups', groups],
  ]);

  const popularSearches: State.InternalLink[] | null = popularSearchesSelector(state);

  const constants = constantsSelector(state);
  const topAdBannerAdUnitPath = constants?.[CONSTANTS.SEARCH_PAGE_TOP_AD_BANNER];
  const bottomAdBannerAdUnitPath = constants?.[CONSTANTS.SEARCH_PAGE_BOTTOM_AD_BANNER];
  const externalAdsConfig: Contentful.ExternalAdsConfig = {
    topAdBanner: topAdBannerAdUnitPath ? { adUnitPath: topAdBannerAdUnitPath } : null,
    bottomAdBanner: bottomAdBannerAdUnitPath ? { adUnitPath: bottomAdBannerAdUnitPath } : null,
  };

  return {
    isAuth: isAuthSelector(state),
    results: searchResultsSelector(state),
    resultsLoading: searchResultsLoadingSelector(state),
    resultsFetched: searchResultsFetchedSelector(state),
    pagination: paginationSelector(state),
    sortBy,
    page,
    perPage,
    searchQuery,
    suggested: suggestedSelector(state),
    filtersStr, // subscription required to fire fetchSearchContent if changed
    analyticsFacets,
    popularSearches,
    skipSearchFrontloadRequest: skipSearchFrontloadRequestSelector(state),
    externalAdsConfig,
  };
};

const frontload = async (props: Props) => {
  const { searchQuery, sortBy, page, perPage, filtersStr, skipSearchFrontloadRequest } = props;
  if (skipSearchFrontloadRequest) return props.resetSkipSearchFrontloadRequest();
  if (!searchQuery && !sortBy && !page && !perPage && !filtersStr) return; // Prevent browser back button click unexpected fetchSearchContent
  return props.fetchSearchContent();
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: Props, newProps: Props) =>
    prevProps.searchQuery !== newProps.searchQuery ||
    prevProps.sortBy !== newProps.sortBy ||
    prevProps.page !== newProps.page ||
    prevProps.perPage !== newProps.perPage ||
    prevProps.filtersStr !== newProps.filtersStr ||
    prevProps.isAuth !== newProps.isAuth,
};

export const PageSearchContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageSearch));
